import React, { useCallback, useEffect, useMemo, useState, Children, cloneElement } from 'react';

// DEPENDENCIES
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import snakeCase from 'lodash/snakeCase';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// GLOBAL VARIABLES
import { INTERFACE, RECORD } from 'pathnames.js';
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// LOCAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// FUNCTIONS/HELPERS
import { documentFetch, fileDownload, makePath, modalFunctions, sleep, checkRole } from 'functions.js';
import { sidebarActions } from 'actions.js';
import usePrevious from 'helpers/usePrevious';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Alert from 'core/tools/alert/Alert';
import Record from 'parts/record/Record';
import Sprite from 'core/tools/Sprite';

// LOCAL COMPONENTS
import Buttons from './Buttons';
import FalloutSidebar from '../tools/FalloutSidebar/FalloutSidebar';
import HeaderData from '../../../tools/HeaderData';
import SalesLogs from '../../sales/parts/SalesLogs';

const RECORD_ENDPOINTS = {
  get: ENDPOINTS.sales.single
};

// MAIN COMPONENT
const AMFinanceDetail = ({ children }) => {
  const [record, setRecord] = useState({});
  const [usePreloader, setUsePreloader] = useState(true);
  const [statusMessage, setStatusMessage] = useState();

  const isFinanceManager = checkRole(['financeManager']);

  const dispatch = useDispatch();

  const { recordID } = useParams();

  const navigate = useNavigate();

  const location = useLocation();
  const isFromNewSale = !!location?.state?.isFromNewSale;

  const { isView } = useRecordView();

  const {
    inventory: { stock_num, vehicle_year, make, model } = {},
    customer_first_name,
    customer_last_name,
    sales_id,
    sales_status_name,
    sale_type_category_code,
    wholesale_format_id
  } = record || {};
  const customerName = `${customer_first_name} ${customer_last_name}`;

  const status = useMemo(() => lowerCase(sales_status_name).replace(/\s/g, ''), [sales_status_name]);

  const isRetail = sale_type_category_code === RETAIL_WHOLESALE.retail.category_code;
  const isWholesaleDetailed =
    sale_type_category_code === RETAIL_WHOLESALE.wholesale.category_code &&
    wholesale_format_id === WHOLESALE_FORMAT.detailed.numeric;

  const newSaleConfirmationIncludeCleanup = useMemo(
    () => isRetail || isWholesaleDetailed,
    [isRetail, isWholesaleDetailed]
  );

  // Only usePreloader once
  useEffect(() => {
    async function init() {
      await sleep(1000);
      setUsePreloader(false);
    }
    init();

    return () => dispatch(sidebarActions.close());
  }, [dispatch]);

  const prevStatus = usePrevious(status);

  useEffect(() => {
    if (prevStatus && prevStatus !== status) {
      setStatusMessage(`Sale status has been changed to ${status}.`);
    }
  }, [prevStatus, status]);

  const handleRecordLoaded = useCallback(r => setRecord(r), []);

  const handleEditClick = !isFinanceManager 
    ? () =>
        navigate(makePath(INTERFACE.sales, RECORD.edit, sales_id), {
          state: {
            prevPath: location.pathname
          }
        })
    : undefined;

  const newSaleConfirmationType = useMemo(
    () => (newSaleConfirmationIncludeCleanup ? 'Gross Recap and Clean-Up Form' : 'Gross Recap'),
    [newSaleConfirmationIncludeCleanup]
  );

  const handlePrint = useCallback(() => {
    const fileType = newSaleConfirmationIncludeCleanup ? 'gross_recap_cleanup' : 'gross_recap';
    const fileName = `${stock_num}_${snakeCase(customerName)}_${fileType}`;
    const args = {
      endpoint: makePath(ENDPOINTS.sales.print.recap, recordID),
      params: {
        recapType: 'Gross',
        printCleanUpForm: newSaleConfirmationIncludeCleanup ? 1 : 0
      },
      accept: 'pdf',
      contentType: 'pdf',
      mimeType: 'application/pdf',
      loadingMessage: `Downloading ${newSaleConfirmationType}`,
      errorMessage: `Unable to download ${newSaleConfirmationType}`,
      successMessage: `${newSaleConfirmationType} downloaded.`,
      onSuccess: ({ url }) => {
        fileDownload(url, fileName, undefined, true, true);
      }
    };
    documentFetch(args);
  }, [customerName, newSaleConfirmationIncludeCleanup, newSaleConfirmationType, recordID, stock_num]);

  const renderConfirmationModal = useCallback(() => {
    modalFunctions.add({
      type: 'confirmation',
      title: 'Sale Confirmation',
      body: (
        <div className="d-flex">
          <Sprite use="check-circle" fill="success" className="me-2" />
          <span className="flex-grow-1">This sale has been successfully added.</span>
        </div>
      ),
      cancelButton: {
        col: { xs: { span: 24, order: 2 }, lg: { span: newSaleConfirmationIncludeCleanup ? 8 : 12, order: 1 } }
      },
      continueButton: {
        label: `Print ${newSaleConfirmationType}`,
        icon: {
          use: 'print'
        },
        onClick: handlePrint,
        col: { xs: { span: 24, order: 1 }, lg: { span: newSaleConfirmationIncludeCleanup ? 16 : 12, order: 2 } },
        className: 'mb-4 mb-lg-0'
      },
      spacing: 'sm'
    });
  }, [handlePrint, newSaleConfirmationIncludeCleanup, newSaleConfirmationType]);

  useEffect(() => {
    function init() {
      if (isFromNewSale && !isEmpty(record)) {
        renderConfirmationModal();
        navigate({ replace: true });
      }
    }
    init();
  }, [dispatch, isFromNewSale, record, renderConfirmationModal, navigate]);

  return (
    <Record.View
      title="AM/Finance"
      endpoints={RECORD_ENDPOINTS}
      onRecordLoaded={handleRecordLoaded}
      headerTools={[
        <HeaderData
          stockNo={stock_num}
          year={vehicle_year}
          make={make}
          model={model}
          disabled={status === 'fallout'}
          onEditClick={handleEditClick}
        />
      ]}
      headerRight={<SalesLogs />}
      usePreloader={usePreloader}
      fixedHeader={isView}
    >
      <Buttons record={record} isFinanceManager={isFinanceManager} />
      {statusMessage ? (
        <Alert.Dismissable variant="primary" message={statusMessage} onClose={() => setStatusMessage(null)} />
      ) : null}
      {Children.map(children, (child, i) =>
        child
          ? cloneElement(child, {
              key: i,
              status
            })
          : null
      )}
      <Buttons record={record} isFinanceManager={isFinanceManager} />
      <FalloutSidebar />
    </Record.View>
  );
};

// EXPORT
export default AMFinanceDetail;
